import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl, toVancouverDateString } from '../lib/utils';
import { APP_TIMEZONE } from '../config';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';

function HighlightedText({ text, query }) {
  if (!query || !text) return <span>{text}</span>;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <span>
      {parts.map((part, i) => 
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-300 dark:bg-yellow-600 px-0.5 rounded">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function ResultCard({ title, subtitle, snippets, query, onClick }) {
  return (
    <div 
      onClick={onClick}
      className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer"
    >
      <h4 className="font-semibold text-foreground mb-1">{title}</h4>
      {subtitle && <p className="text-sm text-muted-foreground mb-2">{subtitle}</p>}
      <div className="space-y-2">
        {snippets.map((snippet, i) => (
          <div key={i} className="text-sm text-muted-foreground bg-secondary/30 rounded p-2">
            <span className="text-xs text-primary font-medium uppercase mr-2">{snippet.field}:</span>
            <HighlightedText text={snippet.text} query={query} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CategorySection({ title, icon, results, query, onResultClick }) {
  if (!results || results.length === 0) return null;
  
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <span>{icon}</span>
        <span>{title}</span>
        <span className="text-sm font-normal text-muted-foreground">({results.length})</span>
      </h3>
      <div className="space-y-4">
        {results.map((result, i) => (
          <ResultCard 
            key={result._id || i}
            {...result}
            query={query}
            onClick={() => onResultClick(result)}
          />
        ))}
      </div>
    </div>
  );
}

function SearchContent({ condo }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { condoSlug } = useParams();
  const { getToken } = useAuth();
  const query = searchParams.get('q') || '';

  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);

  const performSearch = useCallback(async () => {
    if (!query.trim() || !condo?._id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${getApiBaseUrl()}/api/search/condo/${condo._id}?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setResults(response.data.results || {});
      setTotalResults(response.data.totalResults || 0);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query, condo, getToken]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const handleTicketClick = (result) => {
    navigate(`/${condoSlug}/tickets?ticket=${result._id}`);
  };

  const handleBulletinClick = () => {
    navigate(`/${condoSlug}/bulletins`);
  };

  const handleHandbookClick = (result) => {
    navigate(`/${condoSlug}/handbook/${result._id}`);
  };

  const handleForumClick = (result) => {
    navigate(`/${condoSlug}/forum?post=${result._id}`);
  };

  const handleCalendarClick = (result) => {
    const date = toVancouverDateString(new Date(result.startTime));
    navigate(`/${condoSlug}/calendar?date=${date}`);
  };

  const ticketResults = (results.tickets || []).map(t => ({
    ...t,
    title: t.ticketNumber ? `${t.ticketNumber}: ${t.title}` : t.title,
    subtitle: `${t.status.toUpperCase()} • ${t.priority} priority`,
    snippets: t.snippets
  }));

  const bulletinResults = (results.bulletins || []).map(b => ({
    ...b,
    title: b.title,
    subtitle: new Date(b.createdAt).toLocaleDateString('en-US', { timeZone: APP_TIMEZONE }),
    snippets: b.snippets
  }));

  const handbookResults = (results.handbook || []).map(h => ({
    ...h,
    title: h.title,
    snippets: h.snippets
  }));

  const forumResults = (results.forum || []).map(f => ({
    ...f,
    title: `Post by ${f.userName || 'Unknown'}`,
    subtitle: f.content,
    snippets: f.snippets
  }));

  const calendarResults = (results.calendar || []).map(c => ({
    ...c,
    title: c.title,
    subtitle: new Date(c.startTime).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: APP_TIMEZONE
    }),
    snippets: c.snippets
  }));

  return (
    <>
      <h2 className="text-3xl font-bold text-foreground mb-2">Search Results</h2>
      {query && (
        <p className="text-muted-foreground mb-6">
          {loading ? 'Searching...' : `${totalResults} result${totalResults !== 1 ? 's' : ''} for "${query}"`}
        </p>
      )}

      {!query && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="mb-4 text-muted-foreground flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-foreground text-lg">Enter a search term</p>
          <p className="text-muted-foreground text-sm mt-2">
            Use the search box above to find content across tickets, bulletins, handbook, forum, and calendar.
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Searching...</p>
        </div>
      )}

      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {!loading && !error && query && totalResults === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="mb-4 text-muted-foreground flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-foreground text-lg">No results found</p>
          <p className="text-muted-foreground text-sm mt-2">
            Try different keywords or check your spelling.
          </p>
        </div>
      )}

      {!loading && !error && totalResults > 0 && (
        <div>
          <CategorySection
            title="Tickets"
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>}
            results={ticketResults}
            query={query}
            onResultClick={handleTicketClick}
          />
          <CategorySection
            title="Bulletins"
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>}
            results={bulletinResults}
            query={query}
            onResultClick={handleBulletinClick}
          />
          <CategorySection
            title="Handbook"
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
            results={handbookResults}
            query={query}
            onResultClick={handleHandbookClick}
          />
          <CategorySection
            title="Forum"
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>}
            results={forumResults}
            query={query}
            onResultClick={handleForumClick}
          />
          <CategorySection
            title="Calendar"
            icon={<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
            results={calendarResults}
            query={query}
            onResultClick={handleCalendarClick}
          />
        </div>
      )}
    </>
  );
}

function SearchPage() {
  return (
    <CondoLayout>
      {({ condo }) => <SearchContent condo={condo} />}
    </CondoLayout>
  );
}

export default SearchPage;
